import React from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
} from '@mui/material';
import {
  ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import BottomNav from '../components/BottomNavigation';
import useResponsive from '../hooks/useResponsive';

const TermsConditions = () => {
  const navigate = useNavigate();

  return (
    <Box sx={{ backgroundColor: '#f8fafc', minHeight: '100vh', pb: 10 }}>
      {/* Header */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0A2A66 0%, #1a237e 100%)',
          borderBottom: 1,
          borderColor: 'divider',
          py: 2,
          position: 'sticky',
          top: 0,
          zIndex: 100,
          boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
        }}
      >
        <Container maxWidth="lg">
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton onClick={() => navigate(-1)} sx={{ color: 'white' }}>
              <ArrowBack />
            </IconButton>
            <Typography variant="h5" fontWeight="600" color="white">
              Terms & Conditions
            </Typography>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ 
          backgroundColor: 'white', 
          borderRadius: 2, 
          p: 4, 
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          lineHeight: 1.8
        }}>
          <Typography variant="h4" fontWeight="bold" color="#1a237e" gutterBottom>
            NIStylez - Customer Terms & Conditions
          </Typography>
          
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, fontStyle: 'italic' }}>
            Effective Date: 07/10/2025
          </Typography>

          <Typography variant="body1" sx={{ mb: 3 }}>
            These Terms & Conditions ("Agreement") govern your use of the NIStylez platform ("Platform") as a customer. By accessing or using the Platform, you agree to comply with these Terms & Conditions.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            1. Registration and Account Creation
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            1.1 To book services through the Platform, you must create an account with accurate and complete information.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            1.2 You must ensure that your account details, including personal information and payment methods, are kept up to date. You are responsible for maintaining the confidentiality of your account and login details.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            1.3 By registering an account, you agree to receive notifications and updates from NIStylez related to your services, bookings, and promotions.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            2. Booking and Payment
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            2.1 You can browse the available service providers, view their profiles, and make bookings for beauty and wellness services through the Platform.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            2.2 All bookings made via the Platform must be confirmed by the service provider. If the service provider is unavailable, you will be notified, and the booking will be canceled or rescheduled.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            2.3 Payment for services will be processed through the Platform. By booking services, you agree to pay the service provider's set price, along with any applicable service fees or taxes.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            2.4 The price of services is subject to change. You will be informed of the final cost before confirming the booking. Payments will be securely processed via the payment method you provide.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            3. Cancellations and Refunds
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            3.1 You may cancel or reschedule your appointment according to the cancellation policy outlined. Cancellation policies may vary, and any cancellation fees will be indicated at the time of booking.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            3.2 If you need to cancel or reschedule your appointment, you must do so within the specified time frame to avoid being charged the full price for the service.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            3.3 In the event that a service provider fails to attend an appointment or cannot fulfill the service, you are entitled to a full refund, subject to the Platform's refund policy.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            3.4 Refunds for dissatisfaction with the service provided are handled according to the Australian Consumer Law, and you may be eligible for a refund if the service does not meet the agreed-upon standards.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            4. Service Delivery
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            4.1 You agree to provide a safe and suitable environment for the service provider to perform the service, whether in your home or at the service provider's salon.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            4.2 You are responsible for ensuring that you are available and ready for the service at the scheduled time. If the service provider arrives at the appointment location and you are not available, you may be charged a no-show fee.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            4.3 You acknowledge that any damage, injury, or loss caused to the service provider or third parties during the service is your responsibility.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            5. Feedback and Ratings
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            5.1 After receiving a service, you may be asked to rate the service provider and provide feedback on your experience.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            5.2 Your feedback and ratings are important in helping other customers make informed decisions. You agree to provide honest and constructive feedback.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            5.3 NIStylez reserves the right to remove any ratings or reviews that violate our guidelines or contain inappropriate content.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            6. Privacy and Data Protection
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            6.1 Your personal data and booking information will be handled in accordance with the Privacy Act 1988 (Cth) and the Australian Privacy Principles (APPs).
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            6.2 You agree to the collection and use of your personal information for the purpose of processing bookings, payments, and communication related to services.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            6.3 Your payment information will be securely processed by a third-party payment processor, and NIStylez does not store your payment details.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            7. Liability and Disclaimers
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            7.1 NIStylez is a platform that connects customers with service providers and is not responsible for the quality, safety, or performance of services provided. Any issues arising during or after the service are between you and the service provider.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            7.2 NIStylez is not liable for any direct, indirect, incidental, or consequential damages arising from the services provided, including any injury, damage, or dissatisfaction.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            7.3 You agree to indemnify and hold harmless NIStylez, its directors, employees, and affiliates from any claims or liabilities arising from your use of the Platform and services.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            8. Modifications to Terms
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            8.1 NIStylez reserves the right to amend these Terms & Conditions at any time. Changes will be communicated to you through the Platform or email.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            8.2 Your continued use of the Platform after such amendments constitutes your acceptance of the updated Terms & Conditions.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            9. Termination of Account
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            9.1 NIStylez may suspend or terminate your account at any time for violations of these Terms & Conditions or for conduct that is harmful to the Platform, other users, or service providers.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            9.2 You may terminate your account at any time by notifying NIStylez. Termination will not affect any outstanding bookings or obligations under this Agreement.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            10. Compliance with Australian Law
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            10.1 By using the Platform, you agree to comply with all applicable Australian laws and regulations, including those related to consumer protection, privacy, data security, and taxation.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            10.2 You agree not to engage in fraudulent, illegal, or unethical activities on the Platform.
          </Typography>

          <Typography variant="h5" fontWeight="600" color="#1a237e" sx={{ mt: 4, mb: 2 }}>
            11. Dispute Resolution
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            11.1 If you encounter any issues with a service provider, you agree to attempt to resolve the dispute amicably with the service provider.
          </Typography>
          <Typography variant="body1" sx={{ mb: 1 }}>
            11.2 If a dispute cannot be resolved directly, you may escalate the issue to NIStylez for mediation or other appropriate dispute resolution methods.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            11.3 If a resolution is not reached, you may seek resolution through alternative dispute resolution processes or, if necessary, take legal action in accordance with Australian law.
          </Typography>

          <Box sx={{ 
            borderTop: '2px solid #e0e7ff', 
            pt: 3, 
            mt: 4,
            textAlign: 'center'
          }}>
            <Typography variant="body1" fontWeight="600" color="#1a237e">
              By using the NIStylez Platform, you agree to these Terms & Conditions.
            </Typography>
          </Box>
        </Box>
      </Container>

      {/* Bottom Navigation */}
      <BottomNav />
    </Box>
  );
};

export default TermsConditions;